import { Button, TextField } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { HashLoader } from 'react-spinners';
import AlertMessage from '../common/AlertMessage/AlertMessage';

import BrandLogo from '../../branding/BrandLogo';
import { requestUnlockApiCall } from '../../services/api/accountLocking';
import { APP_ROUTES } from '../../utils/constants/appRoutes';
import { validateEmail } from '../../utils/helpers/helpers';
import LegalLinks from '../legal/LegalLinks';
import './style.css';

const RequestUnlock: React.FC = () => {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token') ?? '';
  const emailFromUrl = searchParams.get('email') ?? '';

  const [email, setEmail] = useState(emailFromUrl);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alert, setAlert] = useState<{
    show: boolean;
    type: 'success' | 'error';
    message: string;
  }>({ show: false, type: 'success', message: '' });

  useEffect(() => {
    setEmail(emailFromUrl);
  }, [emailFromUrl]);

  const handleReasonChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setReason(e.target.value);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!tokenFromUrl.trim()) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Invalid or expired link. Please contact support.',
      });
      return;
    }
    if (!validateEmail(email)) {
      setAlert({ show: true, type: 'error', message: 'Please enter a valid email address.' });
      return;
    }

    setLoading(true);
    setAlert(prev => ({ ...prev, show: false }));
    try {
      const response = await requestUnlockApiCall({
        token: tokenFromUrl,
        email: email.trim(),
        reason: reason.trim() || undefined,
      });
      if (response.data?.success) {
        setAlert({
          show: true,
          type: 'success',
          message:
            response.data.message ??
            'Your unlock request has been submitted. A super administrator will review it shortly.',
        });
        setSubmitted(true);
      } else {
        setAlert({
          show: true,
          type: 'error',
          message: 'Something went wrong, please try again later.',
        });
      }
    } catch (error: any) {
      const message =
        error.response?.data?.message ?? 'Invalid or expired link. Please contact support.';
      setAlert({ show: true, type: 'error', message });
    } finally {
      setLoading(false);
    }
  }, [tokenFromUrl, email, reason]);

  const hasValidLink = tokenFromUrl.trim().length > 0 && validateEmail(emailFromUrl);
  const canSubmit = hasValidLink && validateEmail(email) && !loading && !submitted;

  return (
    <div className="limiter">
      <AlertMessage
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert(prev => ({ ...prev, show: false }))}
      />
      <div className="container-login100">
        <div className="wrap-login100">
          <div>
            <BrandLogo />
            <h5 className="Center Margin mb-4">Request Account Unlock</h5>
            <p className="Center Margin mb-4" style={{ fontSize: 14, color: '#666' }}>
              Your account has been locked due to inactivity. Submit a request and a super
              administrator will review it.
            </p>

            {!hasValidLink ? (
              <p className="Center Margin" style={{ fontSize: 14, color: '#d32f2f' }}>
                Invalid or expired link. Please contact support.
              </p>
            ) : (
              <>
                <div className="login-fields-container">
                  <TextField
                    id="request-unlock-email"
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="login-fields"
                    disabled={loading}
                    inputProps={{ readOnly: !!emailFromUrl }}
                  />
                  <TextField
                    id="request-unlock-reason"
                    label="Reason (optional)"
                    multiline
                    rows={3}
                    value={reason}
                    onChange={handleReasonChange}
                    className="login-fields"
                    disabled={loading}
                    placeholder="e.g. I need access to review device data"
                  />
                </div>
                <Button
                  size="medium"
                  variant="contained"
                  className="full-width"
                  disabled={!canSubmit}
                  onClick={handleSubmit}
                >
                  {loading ? (
                    <HashLoader size={30} color="#1b6bae" speedMultiplier={0.8} />
                  ) : (
                    'Submit Unlock Request'
                  )}
                </Button>
              </>
            )}

            <div className="mt-2 auth-nav-link">
              <Link to={APP_ROUTES.AUTH}>Return to Sign In</Link>
            </div>
            <div className="mt-2">
              <LegalLinks shortLabels />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestUnlock;
